/* __next_internal_client_entry_do_not_use__ SDUIGridFixedColumnLayoutBase auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Column } from "@faire/web--source/slate/Layout";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
// eslint-disable-next-line no-restricted-imports
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import chunk from "lodash/chunk";
import React, { useContext, useMemo } from "react";
import { convertServerResponsivePropertyToClientResponsiveProperty, getResponsiveStylingClassNames } from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import { BrandTileRowPlaceholder, ProductTileRowPlaceholder } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/GridRowPlaceholder";
import { SDUIGridHeaderRow } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
import { SDUIGridRow } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridRow";
import * as gridStyles from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/styles.css";
import { getFixedColumnGridStyleVariables, getGridRowGapStyleVariable } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/utils/gridStylingUtils";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { FilterablePaginatedSectionContext } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";
var FALLBACK_NUMBER_OF_TILES_PER_ROW = 2;
export var SDUIGridFixedColumnLayoutBase = function(param) {
    var gridComponent = param.gridComponent, isRequiredForInteractivity = param.isRequiredForInteractivity, loggingData = param.loggingData, renderBehaviour = param.renderBehaviour;
    var _column_layout_fixed, _column_layout_fixed1;
    var _gridComponent_column_layout = gridComponent.column_layout, column_layout = _gridComponent_column_layout === void 0 ? IGridComponent.IColumnLayout.build() : _gridComponent_column_layout, header_background_icon = gridComponent.header_background_icon, log_tile_batch_view = gridComponent.log_tile_batch_view, responsive_header_background_icon = gridComponent.responsive_header_background_icon, responsive_title = gridComponent.responsive_title, _gridComponent_style = gridComponent.style, style = _gridComponent_style === void 0 ? IGridStyle.build() : _gridComponent_style, tiles = gridComponent.tiles, title = gridComponent.title;
    var _style_column_spacing = style.column_spacing, column_spacing = _style_column_spacing === void 0 ? Spacing.SPACING_NONE : _style_column_spacing, _style_row_spacing = style.row_spacing, row_spacing = _style_row_spacing === void 0 ? Spacing.SPACING_NONE : _style_row_spacing;
    var _column_layout_fixed_num_columns;
    var responsiveNumberOfTilesPerRow = convertServerResponsivePropertyToClientResponsiveProperty((_column_layout_fixed_num_columns = (_column_layout_fixed = column_layout.fixed) === null || _column_layout_fixed === void 0 ? void 0 : _column_layout_fixed.num_columns) !== null && _column_layout_fixed_num_columns !== void 0 ? _column_layout_fixed_num_columns : FALLBACK_NUMBER_OF_TILES_PER_ROW, function(col) {
        return col;
    }, (_column_layout_fixed1 = column_layout.fixed) === null || _column_layout_fixed1 === void 0 ? void 0 : _column_layout_fixed1.responsive_num_columns);
    var currentNumberOfTilesPerRow = useResponsiveValue(responsiveNumberOfTilesPerRow);
    var tileChunks = useMemo(function() {
        return chunk(tiles, currentNumberOfTilesPerRow);
    }, [
        currentNumberOfTilesPerRow,
        tiles
    ]);
    return /*#__PURE__*/ _jsxs(Column, {
        className: gridStyles.SDUIGridWrapper,
        children: [
            /*#__PURE__*/ _jsx(SDUIGridHeaderRow, {
                title: title,
                responsiveTitle: responsive_title,
                backgroundIcon: header_background_icon,
                responsiveBackgroundIcon: responsive_header_background_icon,
                loggingData: loggingData
            }),
            tileChunks.map(function(tiles, index) {
                var _tiles__logging_data, _tiles_;
                var forceVisibility = index === 0 && isRequiredForInteractivity;
                var _tiles__logging_data_instance_identifier;
                return /*#__PURE__*/ _jsx(MaybeWhenVisibilityIsImminent, {
                    forceVisibility: forceVisibility,
                    rowPlaceholder: getRowPlaceholder(tiles[0], assignInlineVars(getGridRowGapStyleVariable(index === 0 ? undefined : row_spacing))),
                    renderBehaviour: renderBehaviour,
                    children: /*#__PURE__*/ _jsx(SDUIGridRow, {
                        tiles: tiles,
                        loggingData: loggingData,
                        className: classNames(gridStyles.gridTileContainer, getResponsiveStylingClassNames(responsiveNumberOfTilesPerRow, ResponsiveGridTemplateColumnsClassNames)),
                        componentStyle: assignInlineVars(getFixedColumnGridStyleVariables(responsiveNumberOfTilesPerRow, column_spacing, index === 0 ? undefined : row_spacing)),
                        logTileBatchView: log_tile_batch_view,
                        shouldSetIsScrollingTopToFalseOnView: forceVisibility && (renderBehaviour === null || renderBehaviour === void 0 ? void 0 : renderBehaviour.gridOptimizations)
                    })
                }, (_tiles__logging_data_instance_identifier = (_tiles_ = tiles[0]) === null || _tiles_ === void 0 ? void 0 : (_tiles__logging_data = _tiles_.logging_data) === null || _tiles__logging_data === void 0 ? void 0 : _tiles__logging_data.instance_identifier) !== null && _tiles__logging_data_instance_identifier !== void 0 ? _tiles__logging_data_instance_identifier : index);
            })
        ]
    });
};
var ResponsiveGridTemplateColumnsClassNames = {
    tabletAndAbove: gridStyles.tabletAndAboveGridTemplateColumns,
    desktopAndAbove: gridStyles.desktopAndAboveGridTemplateColumns,
    xLargeAndAbove: gridStyles.xLargeAndAboveGridTemplateColumns
};
var getRowPlaceholder = function(tile, placeholderStyles) {
    if (!tile) {
        return undefined;
    }
    if (tile.brand_tile || tile.brand_tile_with_action) {
        return /*#__PURE__*/ _jsx(BrandTileRowPlaceholder, {
            placeholderStyles: placeholderStyles
        });
    } else if (tile.product_tile || tile.product_with_brand_info_tile) {
        return /*#__PURE__*/ _jsx(ProductTileRowPlaceholder, {
            placeholderStyles: placeholderStyles
        });
    }
    return undefined;
};
var MaybeWhenVisibilityIsImminent = function(param) {
    var forceVisibility = param.forceVisibility, rowPlaceholder = param.rowPlaceholder, children = param.children, renderBehaviour = param.renderBehaviour;
    var isScrollingToTop = useContext(FilterablePaginatedSectionContext).isScrollingToTop;
    if (!forceVisibility && isScrollingToTop && (renderBehaviour === null || renderBehaviour === void 0 ? void 0 : renderBehaviour.gridOptimizations)) {
        if (rowPlaceholder) {
            return rowPlaceholder;
        }
    }
    if (!forceVisibility && rowPlaceholder) {
        return /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
            customizePlaceholder: rowPlaceholder,
            shouldRunNormallyInTest: true,
            children: children
        });
    }
    return children;
};
